import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Database, Package, ShoppingCart, Users, Download, ChevronsLeft, ChevronsRight, Boxes, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const STORAGE_KEY = "sidebar_collapsed_v1";

export default function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { canInstall, promptInstall, isStandalone } = useInstallPrompt();
  const [showHelp, setShowHelp] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* localStorage may be blocked in private mode - not fatal */
    }
  }, [collapsed]);

  const { user } = useAuth();
  const navItems = [
    { to: "/", label: t("nav.products"), icon: Package, testId: "nav-dashboard-link" },
    { to: "/cart", label: t("nav.quotations"), icon: ShoppingCart, testId: "nav-cart-link" },
    { to: "/customers", label: t("nav.customers"), icon: Users, testId: "nav-customers-link" },
    ...(["admin", "sample_warehouse"].includes(user?.role) ? [{ to: "/samples", label: t("nav.samples"), icon: Boxes, testId: "nav-samples-link" }] : []),
    ...(["admin", "ci_admin"].includes(user?.role) ? [{ to: "/ci-reservations", label: t("nav.ci_reservations"), icon: Lock, testId: "nav-ci-reservations-link" }] : []),
  ];

  return (
    <aside
      data-testid="app-sidebar"
      className={`${collapsed ? "w-16" : "w-64"} transition-[width] duration-200 flex-shrink-0 bg-white border-r border-gray-200 min-h-screen flex flex-col ${collapsed ? "px-2 py-6" : "p-6"} relative`}
    >
      {/* Collapse toggle - floats on the right edge so it's reachable in both states */}
      <button
        type="button"
        data-testid="sidebar-collapse-toggle"
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-8 z-10 w-6 h-6 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center text-gray-500 hover:text-brand-600 hover:border-brand-300 transition-colors"
      >
        {collapsed
          ? <ChevronsRight size={14} strokeWidth={1.75} />
          : <ChevronsLeft size={14} strokeWidth={1.75} />}
      </button>

      <Link
        to="/"
        className={`flex items-center gap-2.5 ${collapsed ? "justify-center" : ""}`}
        data-testid="header-logo-link"
        title={collapsed ? "Order Processing System" : undefined}
      >
        <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
          <Database size={18} className="text-brand-600" strokeWidth={1.75} />
        </div>
        {!collapsed && (
          <div className="leading-tight overflow-hidden">
            <div className="font-heading font-bold text-[13px] text-gray-900 whitespace-nowrap">{t("app_title")}</div>
            <div className="font-mono text-[9px] uppercase tracking-label text-gray-400 whitespace-nowrap">{t("app_subtitle")}</div>
          </div>
        )}
      </Link>

      <nav className={`${collapsed ? "mt-8" : "mt-10"} space-y-1`}>
        {navItems.map((item) => {
          const active = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              title={collapsed ? item.label : undefined}
              className={`relative flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-4"} py-2.5 text-[15px] font-semibold transition-colors rounded-md ${
                active
                  ? "bg-[#EAF2FF] text-[#2878E8]"
                  : "text-[#6B7280] hover:bg-gray-100 hover:text-[#273142]"
              }`}
            >
              {/* iter 178ab: 3px 蓝色左侧指示线,仅在激活时显示 */}
              {active && !collapsed && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-[#2878E8]" />}
              <Icon size={16} strokeWidth={1.75} className="flex-shrink-0" />
              {!collapsed && item.label}
            </Link>
          );
        })}
      </nav>

      {!isStandalone && (
        <button
          type="button"
          data-testid="sidebar-install-app-button"
          onClick={() => (canInstall ? promptInstall() : setShowHelp(true))}
          title={collapsed ? t("nav.install_app") : undefined}
          className={`mt-auto flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-4"} py-2.5 text-[15px] font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors rounded-md`}
        >
          <Download size={16} strokeWidth={1.75} className="flex-shrink-0" />
          {!collapsed && t("nav.install_app")}
        </button>
      )}

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent data-testid="sidebar-install-help-dialog">
          <DialogHeader>
            <DialogTitle>Install Desktop App</DialogTitle>
            <DialogDescription className="pt-2 text-left space-y-2">
              <span className="block">Your browser didn't offer an automatic install prompt. You can still add this app manually:</span>
              <span className="block">Chrome / Edge: click the install icon (⊕) at the right of the address bar, or open the browser menu and choose "Install Order Processing System...".</span>
              <span className="block">Safari (macOS): open the Share menu and choose "Add to Dock".</span>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
